// src/pages/PrivacyPolicy.jsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
  Paper,
  Alert,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      {/* Otsikko */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          mb: 4,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight={700} gutterBottom>
            🔒 Tietosuojakäytäntö
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Pakettihuolto Oy | Päivitetty viimeksi: 16. tammikuuta 2026
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 4, md: 6 }, mb: 4 }}>

          {/* 1. Yleistä */}
          <section id="general">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              1. Yleistä
            </Typography>
            <Typography paragraph>
              Tämä tietosuojakäytäntö kuvaa, miten Pakettihuolto Oy ("me", "meidän", "meistä") kerää, käyttää, säilyttää ja suojaa henkilötietojasi, kun käytät alustaamme ja palveluitamme. Noudatamme EU:n yleistä tietosuoja-asetusta (GDPR) sekä Suomen tietosuojalakia.
            </Typography>
            <Typography paragraph>
              Käyttämällä palveluitamme hyväksyt tämän tietosuojakäytännön ehdot. Jos et hyväksy käytäntöä, pyydämme sinua olemaan käyttämättä palveluitamme.
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Rekisterinpitäjä
              </Typography>
              <Typography variant="body2"><strong>Yrityksen nimi:</strong> Pakettihuolto Oy</Typography>
              <Typography variant="body2"><strong>Y-tunnus:</strong> 3219435-6</Typography>
              <Typography variant="body2"><strong>Osoite:</strong> Suomi</Typography>
              <Typography variant="body2"><strong>Sähköposti:</strong> info@pakettihuolto.fi</Typography>
              <Typography variant="body2"><strong>Puhelin:</strong> +358 50 505 8167</Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 2. Kerättävät tiedot */}
          <section id="data-collected">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              2. Mitä tietoja keräämme
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              2.1 Antamasi tiedot
            </Typography>
            <Typography paragraph>
              Keräämme tietoja, joita annat meille suoraan käyttäessäsi palveluitamme:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Yhteystiedot:</strong> Nimi, sähköpostiosoite, puhelinnumero</Typography></li>
              <li><Typography><strong>Osoitetiedot:</strong> Nouto- ja toimitusosoitteet</Typography></li>
              <li><Typography><strong>Tilitiedot:</strong> Käyttäjätunnus, salasana (salattuna)</Typography></li>
              <li><Typography><strong>Maksutiedot:</strong> Laskutustiedot (maksukortin tietoja ei säilytetä palvelimillamme)</Typography></li>
              <li><Typography><strong>Pakettitiedot:</strong> Paketin sisältö, mitat, paino</Typography></li>
              <li><Typography><strong>Viestintä:</strong> Yhteydenotot asiakaspalveluun</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              2.2 Automaattisesti kerätyt tiedot
            </Typography>
            <Typography paragraph>
              Palveluitamme käyttäessäsi keräämme automaattisesti joitakin tietoja:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>IP-osoite ja laitetiedot</Typography></li>
              <li><Typography>Selaintyyppi ja -versio</Typography></li>
              <li><Typography>Käyttötiedot (sivuston käyttö, klikkaukset, istuntojen kesto)</Typography></li>
              <li><Typography>Sijaintitiedot (jos sallit sijaintipalvelut)</Typography></li>
              <li><Typography>Evästeet ja vastaavat tekniikat</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              2.3 Kolmansilta osapuolilta saadut tiedot
            </Typography>
            <Typography paragraph>
              Saatamme saada tietoja kolmansilta osapuolilta, kuten maksupalveluntarjoajilta, tunnistautumispalveluilta ja muilta yhteistyökumppaneilta palvelujemme tarjoamiseksi.
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 3. Tietojen käyttö */}
          <section id="data-use">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              3. Miten käytämme tietojasi
            </Typography>
            <Typography paragraph>
              Käytämme henkilötietojasi seuraaviin tarkoituksiin:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Palvelujen tarjoaminen:</strong> Tilausten käsittely, pakettien toimitus ja seuranta</Typography></li>
              <li><Typography><strong>Asiakasviestintä:</strong> Tilausvahvistukset, toimitusilmoitukset, asiakastuki</Typography></li>
              <li><Typography><strong>Laskutus ja maksaminen:</strong> Maksujen käsittely ja laskutus</Typography></li>
              <li><Typography><strong>Palvelun kehittäminen:</strong> Alustamme parantaminen ja uusien ominaisuuksien kehittäminen</Typography></li>
              <li><Typography><strong>Turvallisuus:</strong> Petosten estäminen, tilien suojaaminen ja turvallisuuden varmistaminen</Typography></li>
              <li><Typography><strong>Lakisääteiset velvoitteet:</strong> Kirjanpito-, vero- ja muiden lakien noudattaminen</Typography></li>
              <li><Typography><strong>Markkinointi:</strong> Suoramarkkinointi suostumuksellasi (voit perua milloin tahansa)</Typography></li>
            </Box>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 4. Käsittelyn oikeusperusta */}
          <section id="legal-basis">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              4. Käsittelyn oikeusperusta
            </Typography>
            <Typography paragraph>
              Käsittelemme henkilötietojasi GDPR:n mukaisilla oikeusperusteilla:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Sopimus:</strong> Palvelusopimuksen täyttäminen kanssasi</Typography></li>
              <li><Typography><strong>Lakisääteinen velvoite:</strong> Kirjanpito-, vero- ja muiden lakien noudattaminen</Typography></li>
              <li><Typography><strong>Oikeutettu etu:</strong> Palvelun kehittäminen, turvallisuus ja petosten estäminen</Typography></li>
              <li><Typography><strong>Suostumus:</strong> Markkinointi ja valinnaiset evästeet</Typography></li>
            </Box>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 5. Tietojen jakaminen */}
          <section id="data-sharing">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              5. Tietojen jakaminen kolmansille osapuolille
            </Typography>
            <Typography paragraph>
              Emme myy henkilötietojasi kolmansille osapuolille. Voimme kuitenkin jakaa tietojasi seuraavissa tilanteissa:
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.1 Palveluntarjoajat ja yhteistyökumppanit
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Kuljettajat ja logistiikkakumppanit (pakettien toimitusta varten)</Typography></li>
              <li><Typography>Maksupalveluntarjoajat (maksujen käsittelyä varten)</Typography></li>
              <li><Typography>Pilvipalveluntarjoajat (tietojen säilytystä varten)</Typography></li>
              <li><Typography>Analytiikka- ja viestintäpalveluntarjoajat</Typography></li>
            </Box>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.2 Vastaanottajat ja lähettäjät
            </Typography>
            <Typography paragraph>
              Jaamme tarvittavia yhteystietoja paketin lähettäjän ja vastaanottajan välillä toimituksen mahdollistamiseksi.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              5.3 Viranomaiset
            </Typography>
            <Typography paragraph>
              Voimme luovuttaa tietoja viranomaisille, jos laki tai viranomaismääräys niin vaatii.
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Tietojen siirto EU/ETA-alueen ulkopuolelle:</strong> Jos siirrämme tietoja EU/ETA-alueen ulkopuolelle, varmistamme, että siirto tapahtuu GDPR:n mukaisesti asianmukaisin suojatoimin (esim. vakiosopimuslausekkeet).
              </Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 6. Säilytysajat */}
          <section id="retention">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              6. Tietojen säilytysajat
            </Typography>
            <Typography paragraph>
              Säilytämme henkilötietojasi vain niin kauan kuin on tarpeen käsittelyn tarkoituksen toteuttamiseksi tai lain vaatimusten noudattamiseksi:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Tilitiedot:</strong> Tilin voimassaoloajan + 12 kuukautta tilin poistamisen jälkeen</Typography></li>
              <li><Typography><strong>Tilaustiedot:</strong> 6 vuotta (kirjanpitolain mukaan)</Typography></li>
              <li><Typography><strong>Maksutiedot:</strong> 6 vuotta (kirjanpitolain mukaan)</Typography></li>
              <li><Typography><strong>Asiakaspalveluviestintä:</strong> 3 vuotta viimeisestä yhteydenotosta</Typography></li>
              <li><Typography><strong>Markkinointitiedot:</strong> Kunnes peruutat suostumuksesi</Typography></li>
              <li><Typography><strong>Lokitiedot:</strong> Enintään 12 kuukautta</Typography></li>
            </Box>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 7. Oikeutesi */}
          <section id="rights">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              7. Oikeutesi tietojesi suhteen
            </Typography>
            <Typography paragraph>
              GDPR:n nojalla sinulla on seuraavat oikeudet henkilötietojesi suhteen:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Tarkastusoikeus:</strong> Oikeus tietää, mitä tietoja sinusta on tallennettu</Typography></li>
              <li><Typography><strong>Oikaisuoikeus:</strong> Oikeus korjata virheelliset tiedot</Typography></li>
              <li><Typography><strong>Poisto-oikeus:</strong> Oikeus pyytää tietojen poistamista ("oikeus tulla unohdetuksi")</Typography></li>
              <li><Typography><strong>Rajoittamisoikeus:</strong> Oikeus rajoittaa tietojen käsittelyä</Typography></li>
              <li><Typography><strong>Siirto-oikeus:</strong> Oikeus saada tietosi koneellisesti luettavassa muodossa</Typography></li>
              <li><Typography><strong>Vastustamisoikeus:</strong> Oikeus vastustaa käsittelyä tietyissä tilanteissa</Typography></li>
              <li><Typography><strong>Suostumuksen peruminen:</strong> Oikeus perua antamasi suostumus milloin tahansa</Typography></li>
              <li><Typography><strong>Valitusoikeus:</strong> Oikeus tehdä valitus tietosuojaviranomaiselle</Typography></li>
            </Box>
            <Typography paragraph sx={{ mt: 2 }}>
              Voit käyttää oikeuksiasi ottamalla yhteyttä meihin osoitteessa <strong>info@pakettihuolto.fi</strong>. Vastaamme pyyntöösi 30 päivän kuluessa.
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Valvontaviranomainen
              </Typography>
              <Typography variant="body2">Tietosuojavaltuutetun toimisto</Typography>
              <Typography variant="body2">PL 800, 00531 Helsinki</Typography>
              <Typography variant="body2">Verkkosivusto: tietosuoja.fi</Typography>
              <Typography variant="body2">Puhelin: 029 566 6700</Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 8. Evästeet */}
          <section id="cookies">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              8. Evästeet ja vastaavat tekniikat
            </Typography>
            <Typography paragraph>
              Käytämme evästeitä ja vastaavia tekniikoita parantaaksemme käyttökokemustasi alustallamme:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography><strong>Välttämättömät evästeet:</strong> Tarpeellisia palvelun toiminnalle (esim. kirjautuminen)</Typography></li>
              <li><Typography><strong>Toiminnalliset evästeet:</strong> Muistavat asetuksesi ja mieltymyksesi</Typography></li>
              <li><Typography><strong>Analytiikkaevästeet:</strong> Auttavat meitä ymmärtämään, miten käytät palveluamme</Typography></li>
              <li><Typography><strong>Markkinointievästeet:</strong> Kohdennetun mainonnan näyttämiseen (vain suostumuksellasi)</Typography></li>
            </Box>
            <Typography paragraph sx={{ mt: 2 }}>
              Voit hallita evästeasetuksiasi selaimesi asetuksista tai alustallamme näytettävästä evästeilmoituksesta.
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 9. Tietoturva */}
          <section id="security">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              9. Tietoturva
            </Typography>
            <Typography paragraph>
              Suojaamme henkilötietojasi asianmukaisin teknisin ja organisatorisin toimenpitein:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Salattu tiedonsiirto (SSL/TLS)</Typography></li>
              <li><Typography>Salasanojen tiivistäminen (hashing)</Typography></li>
              <li><Typography>Käyttöoikeuksien rajoittaminen tarpeen mukaan</Typography></li>
              <li><Typography>Säännölliset tietoturvatarkastukset</Typography></li>
              <li><Typography>Henkilöstön koulutus tietosuoja-asioissa</Typography></li>
              <li><Typography>Varmuuskopiointi ja palautussuunnitelmat</Typography></li>
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Tietoturvaloukkaus:</strong> Jos tietoturvaloukkaus tapahtuu ja se todennäköisesti aiheuttaa merkittävän riskin oikeuksillesi, ilmoitamme asiasta sinulle ja valvontaviranomaiselle 72 tunnin kuluessa.
              </Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 10. Alaikäiset */}
          <section id="minors">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              10. Alaikäisten tietosuoja
            </Typography>
            <Typography paragraph>
              Palveluitamme ei ole suunnattu alle 16-vuotiaille lapsille. Emme tietoisesti kerää henkilötietoja alle 16-vuotiailta ilman huoltajan suostumusta. Jos saat tietää, että alaikäinen on toimittanut meille henkilötietoja, ota yhteyttä meihin tietojen poistamista varten.
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 11. Muutokset */}
          <section id="changes">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              11. Muutokset tietosuojakäytäntöön
            </Typography>
            <Typography paragraph>
              Pidätämme oikeuden päivittää tätä tietosuojakäytäntöä. Merkittävistä muutoksista:
            </Typography>
            <Box component="ul" sx={{ ml: 4 }}>
              <li><Typography>Ilmoitamme verkkosivustollamme päivitetyllä "Päivitetty viimeksi" -päivämäärällä</Typography></li>
              <li><Typography>Tiedotamme rekisteröityneille käyttäjille sähköpostitse</Typography></li>
              <li><Typography>Muutokset tulevat voimaan 30 päivää julkaisemisen jälkeen</Typography></li>
            </Box>
            <Typography paragraph>
              Suosittelemme tarkistamaan tämän käytännön säännöllisesti pysyäksesi ajan tasalla siitä, miten suojaamme tietojasi.
            </Typography>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* 12. Yhteystiedot */}
          <section id="contact">
            <Typography variant="h4" color="primary" gutterBottom fontWeight={600}>
              12. Yhteystiedot
            </Typography>
            <Typography paragraph>
              Jos sinulla on kysyttävää tästä tietosuojakäytännöstä tai haluat käyttää oikeuksiasi, ota meihin yhteyttä:
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Tietosuoja-asiat</Typography>
              <Typography variant="body2">Sähköposti: info@pakettihuolto.fi</Typography>
              <Typography variant="body2">Puhelin: +358 50 505 8167</Typography>
              <Typography variant="body2">Aukioloajat: Maanantai–perjantai, 9:00–17:00 (EET/EEST)</Typography>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Postiosoite</Typography>
              <Typography variant="body2">Pakettihuolto Oy</Typography>
              <Typography variant="body2">Y-tunnus: 3219435-6</Typography>
              <Typography variant="body2">Suomi</Typography>
            </Alert>
          </section>

          <Divider sx={{ my: 4 }} />

          {/* Kuittaus */}
          <Alert severity="success" sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Hyväksyntä
            </Typography>
            <Typography paragraph>
              Käyttämällä Pakettihuolto Oy:n palveluita vahvistat, että olet lukenut ja ymmärtänyt tämän tietosuojakäytännön sekä hyväksyt henkilötietojesi käsittelyn tässä kuvatulla tavalla.
            </Typography>
            <Typography variant="body2">
              <strong>Voimaantulopäivä:</strong> 16. tammikuuta 2026
            </Typography>
            <Typography variant="body2">
              <strong>Versio:</strong> 1.0
            </Typography>
          </Alert>
        </Paper>
      </Container>

      {/* Alatunniste */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4, mt: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" align="center" gutterBottom>
            &copy; 2026 Pakettihuolto Oy. Kaikki oikeudet pidätetään.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Link href="#data-collected" color="inherit" underline="hover">Kerättävät tiedot</Link>
            <Link href="#rights" color="inherit" underline="hover">Oikeutesi</Link>
            <Link href="#cookies" color="inherit" underline="hover">Evästeet</Link>
            <Link href="#contact" color="inherit" underline="hover">Yhteystiedot</Link>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};